/**
 * The route tree, declared in code.
 *
 * Code-based rather than file-based routing: the tree is fourteen screens, it
 * fits on one page, and it keeps the search-parameter schemas next to the routes
 * that own them without a codegen step in the build.
 *
 * Every authenticated route sits under one layout route whose component holds
 * the auth gate, so there is exactly one place that decides "signed in or not"
 * — and one place that renders the restoring state, which matters because a
 * cold reload has to wait for the silent refresh before it can know.
 */

import {
  createRootRoute,
  createRoute,
  createRouter,
  Navigate,
  Outlet,
} from '@tanstack/react-router'

import { useAuth } from './app/auth.tsx'
import { DecaReadSurface } from './shell/DecaReadSurface.tsx'
import { DecaSurface } from './shell/DecaSurface.tsx'
import { LoginScreen } from './routes/LoginScreen.tsx'
import { ResetPasswordScreen } from './routes/ResetPasswordScreen.tsx'
import { PanelScreen } from './routes/PanelScreen.tsx'
import { DecaListScreen } from './routes/DecaListScreen.tsx'
import { DecaEditScreen } from './routes/DecaEditScreen.tsx'
import { DecaDuplicateScreen } from './routes/DecaDuplicateScreen.tsx'
import { DecaCreateScreen } from './routes/DecaCreateScreen.tsx'
import { DecaDetailScreen } from './routes/DecaDetailScreen.tsx'
import { PassengerDetailScreen } from './routes/PassengerDetailScreen.tsx'
import { PassengerEditScreen } from './routes/PassengerEditScreen.tsx'
import { PassengerDuplicateScreen } from './routes/PassengerDuplicateScreen.tsx'
import { GroupScreen } from './routes/GroupScreen.tsx'
import { ImportScreen } from './routes/ImportScreen.tsx'
import { ExternalDecasScreen } from './routes/ExternalDecasScreen.tsx'
import { ExternalDecaDetailScreen } from './routes/ExternalDecaDetailScreen.tsx'
import { AddressBookScreen } from './routes/AddressBookScreen.tsx'
import { AdminOrganisationsScreen } from './routes/AdminOrganisationsScreen.tsx'
import { AdminUsersScreen } from './routes/AdminUsersScreen.tsx'
import { AdminApiKeysScreen } from './routes/AdminApiKeysScreen.tsx'
import { AdminServiceAccountsScreen } from './routes/AdminServiceAccountsScreen.tsx'
import { AdminAuditScreen } from './routes/AdminAuditScreen.tsx'
import { AdminCompanyScreen } from './routes/AdminCompanyScreen.tsx'
import { ErrorScreen } from './routes/ErrorScreen.tsx'
import { NotFoundScreen } from './routes/NotFoundScreen.tsx'
import { ProfileScreen } from './routes/ProfileScreen.tsx'
import {
  addressBookSearchSchema,
  decaListSearchSchema,
  groupSearchSchema,
  importSearchSchema,
  loginSearchSchema,
  pagedSearchSchema,
  resetSearchSchema,
} from './routes/searchSchemas.ts'
import { ProgressBar } from './ui/primitives.tsx'

function RootLayout() {
  return <Outlet />
}

function AuthenticatedLayout() {
  const { status } = useAuth()

  if (status === 'restoring') {
    return (
      <div className="auth-screen">
        <div style={{ width: 240 }}>
          <ProgressBar />
        </div>
      </div>
    )
  }

  if (status === 'anonymous') return <Navigate to="/login" />

  return <Outlet />
}

const rootRoute = createRootRoute({ component: RootLayout })

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: LoginScreen,
  validateSearch: loginSearchSchema,
})

// A sibling of `/login`, not a child of the authenticated layout: the whole point of this
// route is that the user cannot sign in yet, so it must not sit behind `AuthenticatedLayout`.
const resetRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/restablecer',
  component: ResetPasswordScreen,
  validateSearch: resetSearchSchema,
})

const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'authenticated',
  component: AuthenticatedLayout,
})

// Two pathless guards: they add a check, not a URL segment.
//
// `deca-surface` is the **tenant** surface: the Panel, and the screens that exist only to write —
// create, edit a draft, group, import, the external DeCAs. The platform roles do not have it.
// `deca-read-surface` is the list and the detail screens — both document types — and the
// «Libreta de direcciones», which every signed-in role may see. Screens here write too: the list
// voids; a detail screen generates, completes, modifies, reassigns and voids; the address book
// keeps its entries and hides. Each draws those for `writeDecas` or `voidDecas` only, and it is that
// check, not this guard, that keeps them from the platform roles: SUPER_ADMIN holds `deca:write` on
// the server.
const decaSurfaceRoute = createRoute({
  getParentRoute: () => appRoute,
  id: 'deca-surface',
  component: DecaSurface,
})

const decaReadSurfaceRoute = createRoute({
  getParentRoute: () => appRoute,
  id: 'deca-read-surface',
  component: DecaReadSurface,
})

const panelRoute = createRoute({
  getParentRoute: () => decaSurfaceRoute,
  path: '/',
  component: PanelScreen,
})

const decasRoute = createRoute({
  getParentRoute: () => decaReadSurfaceRoute,
  path: '/decas',
  component: DecaListScreen,
  validateSearch: decaListSearchSchema,
})

const decaCreateRoute = createRoute({
  getParentRoute: () => decaSurfaceRoute,
  path: '/decas/nueva',
  component: DecaCreateScreen,
})

// #90 — editing a draft. On the WRITE surface although the document it edits is read behind the
// other guard: editing is a write, and the router draws that line structurally rather than
// leaving it to a conditional inside a read screen. It is also the precedence case this tree
// keeps having to answer — TanStack ranks static segments above dynamic ones, and `router.test.ts`
// pins that `/decas/$decaId/editar` lands here rather than being swallowed by `/decas/$decaId`.
const decaEditRoute = createRoute({
  getParentRoute: () => decaSurfaceRoute,
  path: '/decas/$decaId/editar',
  component: DecaEditScreen,
})

const decaGroupRoute = createRoute({
  getParentRoute: () => decaSurfaceRoute,
  path: '/decas/agrupar',
  component: GroupScreen,
  validateSearch: groupSearchSchema,
})

const decaDetailRoute = createRoute({
  getParentRoute: () => decaReadSurfaceRoute,
  path: '/decas/$decaId',
  component: DecaDetailScreen,
})

// A sibling of `/decas/$decaId`, not a branch of it: a *hoja de ruta* is not a DeCA. They are
// different legal instruments under different Orders (FOM/1230/2013 against FOM/2861/2012), and
// the URL should not assert otherwise. Read surface, same as the DeCA detail — every signed-in
// role may look at a document; none of the platform ones may write one.
const passengerDetailRoute = createRoute({
  getParentRoute: () => decaReadSurfaceRoute,
  path: '/hojas-de-ruta/$sheetId',
  component: PassengerDetailScreen,
})

// #90 — editing a route-sheet draft, on the WRITE surface for the reason `decaEditRoute` states.
// The precedence question again, and this one was not theoretical: before this route existed
// `/hojas-de-ruta/$sheetId/editar` resolved under the READ guard, swallowed by the detail route
// above, which `router.test.ts` caught by asserting the guard rather than the component.
const passengerEditRoute = createRoute({
  getParentRoute: () => decaSurfaceRoute,
  path: '/hojas-de-ruta/$sheetId/editar',
  component: PassengerEditScreen,
})

// #178 — «Duplicar». On the WRITE surface for the reason `decaEditRoute` states: the copy is a
// create, whatever the source's status, and the source is only read. The same precedence question
// as `/editar`: these must not be swallowed by the detail routes, which `router.test.ts` pins.
const decaDuplicateRoute = createRoute({
  getParentRoute: () => decaSurfaceRoute,
  path: '/decas/$decaId/duplicar',
  component: DecaDuplicateScreen,
})

const passengerDuplicateRoute = createRoute({
  getParentRoute: () => decaSurfaceRoute,
  path: '/hojas-de-ruta/$sheetId/duplicar',
  component: PassengerDuplicateScreen,
})

const importRoute = createRoute({
  getParentRoute: () => decaSurfaceRoute,
  path: '/importar',
  component: ImportScreen,
  validateSearch: importSearchSchema,
})

// The external-DeCA surface. Under the **tenant** guard, not the read one: recording somebody
// else's document to discharge your own retention duty is an act inside one company, and the
// platform roles do not have a company of their own to do it in. `/decas-externas` is its own
// top-level segment rather than `/decas/externas`, so it cannot be confused with a document id.
const externalDecasRoute = createRoute({
  getParentRoute: () => decaSurfaceRoute,
  path: '/decas-externas',
  component: ExternalDecasScreen,
  validateSearch: pagedSearchSchema,
})

const externalDecaDetailRoute = createRoute({
  getParentRoute: () => decaSurfaceRoute,
  path: '/decas-externas/$externalDecaId',
  component: ExternalDecaDetailScreen,
})

// The «Libreta de direcciones» (#159). Under the **read** guard, unlike the external DeCAs: the
// platform roles read a company's book from inside it, as they read its documents, and write
// nothing there (spec §2). The screen draws its writes for `writeDecas` only, and the server
// refuses them to SUPPORT whatever it draws.
const addressBookRoute = createRoute({
  getParentRoute: () => decaReadSurfaceRoute,
  path: '/libreta',
  component: AddressBookScreen,
  validateSearch: addressBookSearchSchema,
})

const adminOrganisationsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/admin/empresas',
  component: AdminOrganisationsScreen,
  validateSearch: pagedSearchSchema,
})

const adminUsersRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/admin/usuarios',
  component: AdminUsersScreen,
  validateSearch: pagedSearchSchema,
})

const adminApiKeysRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/admin/api',
  component: AdminApiKeysScreen,
})

const adminServiceAccountsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/admin/cuentas-de-servicio',
  component: AdminServiceAccountsScreen,
})

const adminAuditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/admin/auditoria',
  component: AdminAuditScreen,
  validateSearch: pagedSearchSchema,
})

const adminCompanyRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/admin/mi-empresa',
  component: AdminCompanyScreen,
})

const profileRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/perfil',
  component: ProfileScreen,
})

const routeTree = rootRoute.addChildren([
  loginRoute,
  resetRoute,
  appRoute.addChildren([
    // `/decas/nueva` and `/decas/agrupar` now sit under a *different* guard from
    // `/decas/$decaId`, which makes the precedence question sharper rather than softer: it is
    // TanStack Router ranking static path segments above dynamic ones while it processes the
    // tree that keeps `/decas/nueva` a screen instead of a document whose id is "nueva", and
    // that ranking is neither declaration order nor nesting (pinned in `src/test/router.test.ts`).
    decaSurfaceRoute.addChildren([
      panelRoute,
      decaCreateRoute,
      decaEditRoute,
      passengerEditRoute,
      decaDuplicateRoute,
      passengerDuplicateRoute,
      decaGroupRoute,
      importRoute,
      externalDecasRoute,
      externalDecaDetailRoute,
    ]),
    decaReadSurfaceRoute.addChildren([decasRoute, decaDetailRoute, passengerDetailRoute, addressBookRoute]),
    adminOrganisationsRoute,
    adminUsersRoute,
    adminApiKeysRoute,
    adminServiceAccountsRoute,
    adminAuditRoute,
    adminCompanyRoute,
    profileRoute,
  ]),
])

export const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  scrollRestoration: true,
  // Router-level rather than per-route: the paths that need it are the ones no route claims, so
  // there is no route to hang it on. Without it TanStack renders `<p>Not Found</p>` — untranslated,
  // unstyled, outside `AppShell` — and warns about the missing option on every occurrence.
  defaultNotFoundComponent: NotFoundScreen,
  // Router-level for the opposite reason: every route needs it, and a `default` is how every route
  // gets its own catch boundary. Without it a screen that throws while rendering reaches TanStack's
  // built-in `ErrorComponent` — English, outside `AppShell`, and showing the raw exception message.
  defaultErrorComponent: ErrorScreen,
})

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}
